import { get, ref } from 'firebase/database';
import { sendEmailVerification } from 'firebase/auth';
import { auth, database } from '../firebase';

// Read only setup fields, never the company's applicants, jobs or employee data.
export async function readEmployerSetup(uid) {
  const fields = ['companyName', 'city', 'country', 'description', 'ownerFirstName', 'ownerLastName', 'accountSetup'];
  const snapshots = await Promise.all(fields.map(field => get(ref(database, `companies/${uid}/${field}`))));
  const profile = Object.fromEntries(fields.map((field, index) => [field, snapshots[index].val()]));
  const hasCompanyName = Boolean(profile.companyName?.trim());
  if (!hasCompanyName && !profile.accountSetup) {
    const collaborator = await get(ref(database, `collaborators/${uid}`));
    if (collaborator.exists()) return { ...profile, complete: true, companyId: collaborator.val().companyId };
  }
  // Profiles created before this flow have no accountSetup marker.
  return { ...profile, companyId: uid, complete: hasCompanyName && (profile.accountSetup
    ? profile.accountSetup.status === 'complete' && Boolean(profile.city?.trim() && profile.country?.trim())
    : true) };
}

export function safeAuthDestination(from) {
  const path = from?.pathname;
  if (!path || !path.startsWith('/') || path.startsWith('//') || /[\\\s]/.test(path) ||
      ['/login', '/signup', '/forgot-password', '/onboarding', '/verify-email'].includes(path)) return '/';
  return `${path}${from.search || ''}${from.hash || ''}`;
}

export function authErrorKey(error) {
  return ({
    'auth/invalid-credential': 'errors.credentials',
    'auth/user-not-found': 'errors.credentials',
    'auth/wrong-password': 'errors.credentials',
    'auth/invalid-email': 'errors.email',
    'auth/email-already-in-use': 'errors.emailInUse',
    'auth/weak-password': 'errors.password',
    'auth/password-does-not-meet-requirements': 'errors.passwordPolicy',
    'auth/too-many-requests': 'errors.tooManyRequests',
    'auth/network-request-failed': 'errors.network',
    'auth/user-disabled': 'errors.disabled',
    'auth/requires-recent-login': 'errors.reauthenticate',
    'auth/unauthorized-continue-uri': 'errors.emailDelivery',
    'auth/invalid-continue-uri': 'errors.emailDelivery',
    'auth/quota-exceeded': 'errors.emailDelivery',
  })[error?.code] || 'errors.generic';
}

export async function sendVerification(user, language) {
  auth.languageCode = language;
  try {
    await sendEmailVerification(user, { url: `${window.location.origin}/verify-email` });
  } catch (error) {
    // A missing authorized return domain must not prevent email verification.
    // Firebase's hosted action page can still verify the address without a return URL.
    if (error?.code !== 'auth/unauthorized-continue-uri') throw error;
    await sendEmailVerification(user);
  }
  try { sessionStorage.setItem(`verification-sent:${user.uid}`, String(Date.now())); } catch (_) { /* Storage is optional. */ }
}

export function verificationWait(uid) {
  try {
    const sent = Number(sessionStorage.getItem(`verification-sent:${uid}`) || 0);
    return Math.max(0, Math.ceil((sent + 60000 - Date.now()) / 1000));
  } catch (_) { return 0; }
}

export async function syncAuthCookie(user) {
  if (!user?.emailVerified) return;
  const token = await user.getIdToken();
  if (auth.currentUser?.uid !== user.uid) return;
  const domain = window.location.hostname === 'jobseeqr.co' || window.location.hostname.endsWith('.jobseeqr.co')
    ? '; domain=.jobseeqr.co' : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `auth_token=${token}; path=/${domain}${secure}; SameSite=Lax`;
}

export function clearAuthCookie() {
  document.cookie = 'auth_token=; path=/; domain=.jobseeqr.co; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
}
